import { useNavigate } from 'react-router-dom';
import { useCompanyCurrency } from '@packages/core/company/focused-company-context.ts';
import { formatCurrency } from '@packages/core/utils/currency-formatter.ts';
import Table from '@packages/ui/table/table.tsx';
import TableRowColumn from '@packages/ui/table/table-row-cell.tsx';
import { FragmentType, getFragmentData } from '@client/graphql/types';
import { TableSection } from '@packages/ui/table/table-section.tsx';
import TableHeaderColumn from '@packages/ui/table/table-header-column.tsx';
import TableRow from '@packages/ui/table/table-row.tsx';
import { TableCellAlignment } from '@packages/ui/table/table-type.ts';
import { MenuSectionFragment } from '@packages/network-graphql/common/menu-section-fragment.ts';
import { MenuProductConfigurationFragment } from '@packages/network-graphql/common/menu-product-configuration-fragment.ts';
import { MenuProductConfigurationValueFragment } from '@packages/network-graphql/common/menu-product-configuration-value-fragment.ts';
import { useTranslate } from '@tolgee/react';
import { naturalCompare } from '@client/module/utils/natural-compare.ts';
import { EyeIcon, EyeSlashIcon, PencilIcon, TrashIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import DotStatus from '@packages/ui/status/dot-status';
import {
  groupServiceItemsBySection,
  ServiceBySection,
} from '@client/page/management/appointment/service/service-detail/component/group-service-item-by-section.tsx';
import { ProductConfigurationType, ServiceItemFragment } from '@packages/network-graphql/graphql/types/graphql.ts';
import { useDialog } from '@packages/ui/modal/use-dialog.tsx';
import { useNotificationCenter } from '@packages/ui/notification/notification-center.ts';
import useUpdateServiceProduct from '@client/page/management/appointment/service/service-product/service-product-edit/logic/use-update-service-product.ts';
import useDeleteService from '@client/page/management/appointment/service/service-product/service-product-edit/logic/use-delete-service.ts';
import { useDuplicateAppointmentMenuProduct } from '@client/page/management/appointment/service/service-product/service-product-edit/logic/use-duplicate-appointment-menu-product.ts';
import { useUpdateServiceMenuSection } from '@client/page/management/appointment/service/service-section/logic/use-update-service-menu-section.ts';
import { useDeleteServiceMenuSection } from '@client/page/management/appointment/service/service-section/logic/use-delete-service-menu-section.ts';
import { MenuSectionTimeRuleUnion, MenuSectionTimeRuleType } from '@client/graphql/types/graphql';

export default function ServiceTable(props: {
  products: ServiceItemFragment[];
  sections: FragmentType<typeof MenuSectionFragment>[];
}) {
  const { t } = useTranslate();
  const sections = getFragmentData(MenuSectionFragment, props.sections);
  const sortedProductMenuBySections = groupServiceItemsBySection(props.products, sections).sort((a, b) =>
    naturalCompare(a.section?.code ?? '', b.section?.code ?? ''),
  );
  return (
    <div className="overflow-scroll">
      <Table
        data={sortedProductMenuBySections}
        build={(group) => <Group key={group.section?.id ?? 'no-section'} group={group} />}
      >
        <TableHeaderColumn className="w-5" />
        <TableHeaderColumn>{t('common.table-header-column.product')}</TableHeaderColumn>
        <TableHeaderColumn className="w-32 text-right">{t('common.table-header-column.actions')}</TableHeaderColumn>
      </Table>
    </div>
  );
}

function Group(props: { group: ServiceBySection }) {
  const { group } = props;
  const currency = useCompanyCurrency();
  const navigate = useNavigate();
  const { t } = useTranslate();
  const dialog = useDialog();
  const { showAlert } = useNotificationCenter();
  const [updateServiceProduct, { loading: updatingProduct }] = useUpdateServiceProduct();
  const [deleteService, { loading: deletingProduct }] = useDeleteService();
  const [duplicateAppointmentMenuProduct, { loading: duplicatingProduct }] = useDuplicateAppointmentMenuProduct();
  const [updateServiceMenuSection, { loading: updatingSection }] = useUpdateServiceMenuSection();
  const [deleteServiceMenuSection, { loading: deletingSection }] = useDeleteServiceMenuSection();

  // Helper function để process time rule data
  function processTimeRuleData(timeRule: MenuSectionTimeRuleUnion | null | undefined) {
    if (!timeRule) {
      return {
        type: MenuSectionTimeRuleType.Always,
      };
    }

    if (timeRule.__typename === 'MenuSectionAlwaysTimeRule') {
      return {
        type: timeRule.type,
      };
    }

    if (timeRule.__typename === 'MenuSectionRangeTimeRule') {
      return {
        type: timeRule.type,
        start: timeRule.start,
        end: timeRule.end,
        daysOfWeek: timeRule.daysOfWeek ?? [],
        isAllDays: timeRule.isAllDays ?? false,
      };
    }

    return {
      type: MenuSectionTimeRuleType.Always,
    };
  }

  // Handler: Toggle enabled for section
  const handleToggleSectionEnabled = async () => {
    if (!group.section) return;

    try {
      await updateServiceMenuSection({
        variables: {
          id: group.section.id,
          input: {
            code: group.section.code,
            name: group.section.name,
            description: group.section.description,
            inStock: group.section.inStock,
            enabled: !group.section.enabled,
          },
          timeRuleInput: processTimeRuleData(group.section.timeRule),
        },
      });

      showAlert({
        status: 'success',
        title: t('common.success'),
        message: t('Cập nhật trạng thái thành công!'),
      });
    } catch (error) {
      showAlert({
        status: 'error',
        title: t('common.error'),
        message: t('Cập nhật trạng thái thất bại!'),
      });
    }
  };

  // Handler: Delete section
  const handleDeleteSection = () => {
    if (!group.section) return;

    dialog.destructAlert({
      title: t('service.service-detail.section.edit.extra-action.delete.alert.title', 'Xác nhận xóa'),
      message: t(
        'service.service-detail.section.edit.extra-action.delete.alert.message',
        'Bạn có chắc muốn xóa category này?',
      ),
      textButton: t('service.service-detail.section.edit.extra-action.delete.alert.text-button', 'Xóa'),
      onConfirm: async () => {
        try {
          await deleteServiceMenuSection({ variables: { id: group.section?.id ?? '' } });

          showAlert({
            status: 'success',
            title: t('common.success'),
            message: t('Xóa category thành công!'),
          });
        } catch (error) {
          showAlert({
            status: 'error',
            title: t('common.error'),
            message: t('Xóa category thất bại!'),
          });
        }
      },
    });
  };

  // Handler: Toggle enabled for product
  const handleToggleProductEnabled = async (menuProductItem: ServiceItemFragment) => {
    try {
      const configurations = getFragmentData(MenuProductConfigurationFragment, menuProductItem.configurations);
      const baseConfig = configurations[0];
      const baseValues = baseConfig?.values
        ? getFragmentData(MenuProductConfigurationValueFragment, baseConfig.values)
        : [];
      const baseValue = baseValues[0];

      await updateServiceProduct({
        variables: {
          id: menuProductItem.id,
          input: {
            duration: menuProductItem.appointmentMenuProductData.duration,
            menuProduct: {
              code: menuProductItem.code,
              title: menuProductItem.title,
              description: menuProductItem.description,
              enabled: !menuProductItem.enabled,
              inStock: menuProductItem.inStock,
              sectionID: menuProductItem.sectionId,
              configurations: [
                {
                  id: baseConfig?.id ?? '',
                  title: 'Base',
                  type: ProductConfigurationType.Base,
                  values: [
                    {
                      id: baseValue?.id ?? '',
                      name: 'Base',
                      price: baseValue?.price ?? 0,
                    },
                  ],
                },
              ],
            },
          },
        },
      });

      showAlert({
        status: 'success',
        title: t('common.success'),
        message: t('Cập nhật trạng thái thành công!'),
      });
    } catch (error) {
      showAlert({
        status: 'error',
        title: t('common.error'),
        message: t('Cập nhật trạng thái thất bại!'),
      });
    }
  };

  // Handler: Duplicate product
  const handleDuplicateProduct = (menuProductItem: ServiceItemFragment) => {
    dialog.confirmAlert({
      title: t('service.service-detail.product.edit.extra-action.duplicate.confirm.title', 'Nhân bản sản phẩm dịch vụ'),
      message: t(
        'service.service-detail.product.edit.extra-action.duplicate.confirm.message',
        'Bạn có chắc muốn nhân bản sản phẩm dịch vụ này?',
      ),
      textButton: t('service.service-detail.product.edit.extra-action.duplicate.confirm.button', 'Nhân bản'),
      onConfirm: async () => {
        try {
          const result = await duplicateAppointmentMenuProduct({ variables: { id: menuProductItem.id } });
          showAlert({
            status: 'success',
            title: t('common.success'),
            message: t('Nhân bản sản phẩm dịch vụ thành công!'),
          });

          // Try to focus on the newly created product
          const duplicatedProductId = result.data?.duplicateAppointmentMenuProduct.id;
          if (duplicatedProductId) {
            // Wait for the DOM to update after refetch
            setTimeout(() => {
              const newProductElement = document.querySelector(`[data-product-id="${duplicatedProductId}"]`);
              if (newProductElement) {
                (newProductElement as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
                (newProductElement as HTMLElement).focus();
              }
            }, 500);
          }
        } catch (error: unknown) {
          let message = t('Nhân bản sản phẩm dịch vụ thất bại!');
          if (isErrorWithMessage(error)) {
            message = error.message;
          }
          showAlert({
            status: 'error',
            title: t('common.error'),
            message,
          });
        }
      },
    });
  };

  // Handler: Delete product
  const handleDeleteProduct = (menuProductItem: ServiceItemFragment) => {
    dialog.destructAlert({
      title: t('service.service-detail.product.edit.extra-action.delete.alert.title'),
      message: t('service.service-detail.product.edit.extra-action.delete.alert.message'),
      textButton: t('service.service-detail.product.edit.extra-action.delete.alert.text-button'),
      onConfirm: async () => {
        try {
          await deleteService({ variables: { id: menuProductItem.id } });

          showAlert({
            status: 'success',
            title: t('common.success'),
            message: t('Xóa service thành công!'),
          });
        } catch (error: unknown) {
          let message = t('Xóa service thất bại!');
          if (isErrorWithMessage(error)) {
            message = error.message;
          }
          showAlert({
            status: 'error',
            title: t('common.error'),
            message,
          });
        }
      },
    });
  };

  return (
    <>
      <TableSection key={group.section ? group.section.id : 'no-section'} colSpan={3}>
        <div className="flex items-center justify-center gap-x-3 w-full py-2">
          {/* DotStatus column for section: empty for now, or you can add a status if needed */}
          <div className="flex flex-wrap items-center gap-1 font-semibold text-indigo-700">
            {/* <div className="w-4 md:w-11"></div> */}
            <span>
              {group.section?.code ? `${group.section.code} -` : t('menu.menu-detail.table-product.no-code', 'No code')}
              {group.section && !group.section.code && t('menu.menu-detail.table-product.no-position')}
              {!group.section && t('menu.menu-detail.table-product.emoty', 'Category empty')}
            </span>
            {group.section && group.section.name && <span>{group.section.name}</span>}
            {group.section && !group.section.inStock && (
              <span className="border py-1 px-2 rounded-full border-red-600 bg-red-100 text-red-600 text-xs ml-2">
                {t('menu-product.card.out-of-stock', 'Out of stock')}
              </span>
            )}
          </div>
          {/* Right: Quick Actions */}
          <div className="flex items-center gap-2 justify-end">
            {group.section && (
              <PencilIcon
                tabIndex={0}
                aria-label="Edit"
                role="button"
                className="w-5 h-5 text-blue-500 cursor-pointer"
                onClick={() => {
                  // Navigate to section edit page
                  navigate(`sections/${group.section?.id ?? ''}/edit`);
                }}
              />
            )}
            {/* Hide/Show on menu section */}
            {group.section &&
              (group.section.enabled ? (
                <EyeIcon
                  tabIndex={0}
                  aria-label="Visible"
                  role="button"
                  className={`w-5 h-5 text-yellow-500 cursor-pointer ${
                    updatingSection ? 'pointer-events-none opacity-50' : ''
                  }`}
                  onClick={() => {
                    void handleToggleSectionEnabled();
                  }}
                />
              ) : (
                <EyeSlashIcon
                  tabIndex={0}
                  aria-label="Hidden"
                  role="button"
                  className={`w-5 h-5 text-yellow-500 cursor-pointer ${
                    updatingSection ? 'pointer-events-none opacity-50' : ''
                  }`}
                  onClick={() => {
                    void handleToggleSectionEnabled();
                  }}
                />
              ))}
            {/* Delete section */}
            {group.section && (
              <TrashIcon
                tabIndex={0}
                aria-label="Delete"
                role="button"
                className={`w-5 h-5 text-red-500 cursor-pointer ${
                  deletingSection ? 'pointer-events-none opacity-50' : ''
                }`}
                onClick={handleDeleteSection}
              />
            )}
          </div>
        </div>
      </TableSection>

      {group.items
        .sort((a, b) => naturalCompare(a.code, b.code))
        .map((menuProductItem) => {
          const soldOutConditional = !group.section?.inStock || !menuProductItem.inStock;
          const basePrice = formatCurrency(
            getFragmentData(MenuProductConfigurationFragment, menuProductItem.configurations)
              .find((config) => config.type.includes('BASE'))
              ?.values.map((configValue) => getFragmentData(MenuProductConfigurationValueFragment, configValue))
              .at(0)?.price ?? 0,
            currency,
          );

          return (
            <TableRow key={menuProductItem.id} data-product-id={menuProductItem.id}>
              {/* DotStatus column */}
              <TableRowColumn>
                <DotStatus enabled={menuProductItem.enabled} />
              </TableRowColumn>
              {/* Left: Info */}
              <TableRowColumn>
                <div className="flex flex-col gap-1 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{menuProductItem.code}</span>
                    <span className="text-gray-700 line-clamp-2">{menuProductItem.title}</span>
                    {soldOutConditional && (
                      <span className="hidden md:block border py-1 px-2 rounded-full border-red-600 bg-red-100 text-red-600 text-xs ml-2">
                        {t('menu-product.card.out-of-stock', 'Out of stock')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-gray-500 text-sm">
                    {getFragmentData(MenuProductConfigurationFragment, menuProductItem.configurations).some(
                      (config) => config.values.length > 1,
                    ) && <span>{t('Start at')}</span>}
                    <span>{basePrice}</span>
                    {soldOutConditional && (
                      <span className="block md:hidden border py-1 px-2 rounded-full border-red-600 bg-red-100 text-red-600 text-xs">
                        {t('menu-product.card.out-of-stock', 'Out of stock')}
                      </span>
                    )}
                  </div>
                </div>
              </TableRowColumn>
              {/* Right: Quick Actions */}
              <TableRowColumn alignment={TableCellAlignment.Right}>
                <div className="flex items-center gap-2 justify-end">
                  <PencilIcon
                    onClick={() => {
                      navigate(`products/${menuProductItem.id}`);
                    }}
                    tabIndex={0}
                    aria-label="Edit"
                    role="button"
                    className="w-5 h-5 text-blue-500 cursor-pointer"
                  />
                  {/* Duplicate */}
                  <DocumentDuplicateIcon
                    tabIndex={0}
                    aria-label="Duplicate"
                    role="button"
                    className={`w-5 h-5 text-green-500 cursor-pointer ${
                      duplicatingProduct ? 'pointer-events-none opacity-50' : ''
                    }`}
                    onClick={() => {
                      handleDuplicateProduct(menuProductItem);
                    }}
                  />
                  {/* Hide/Show on menu */}
                  {menuProductItem.enabled ? (
                    <EyeIcon
                      tabIndex={0}
                      aria-label="Visible"
                      role="button"
                      className={`w-5 h-5 text-yellow-500 cursor-pointer ${
                        updatingProduct ? 'pointer-events-none opacity-50' : ''
                      }`}
                      onClick={() => {
                        void handleToggleProductEnabled(menuProductItem);
                      }}
                    />
                  ) : (
                    <EyeSlashIcon
                      tabIndex={0}
                      aria-label="Hidden"
                      role="button"
                      className={`w-5 h-5 text-yellow-500 cursor-pointer ${
                        updatingProduct ? 'pointer-events-none opacity-50' : ''
                      }`}
                      onClick={() => {
                        void handleToggleProductEnabled(menuProductItem);
                      }}
                    />
                  )}
                  {/* Delete */}
                  <TrashIcon
                    tabIndex={0}
                    aria-label="Delete"
                    role="button"
                    className={`w-5 h-5 text-red-500 cursor-pointer ${
                      deletingProduct ? 'pointer-events-none opacity-50' : ''
                    }`}
                    onClick={() => {
                      handleDeleteProduct(menuProductItem);
                    }}
                  />
                </div>
              </TableRowColumn>
            </TableRow>
          );
        })}
    </>
  );
}

// Helper type guard for error
function isErrorWithMessage(error: unknown): error is { message: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  );
}
